import { HttpBackend, HttpClient } from '@angular/common/http';
import { Preferences } from '@capacitor/preferences';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AppuserService } from '../appuser/appuser.service';
import { StorageService } from '../storage/storage.service';
import { UtilService } from '../util/util.service';
import { Platform } from '@ionic/angular';
import { RinknetAttachment } from 'src/app/models/rnAttachment.model';
import { AppSettingsService } from '../app-settings/app-settings.service';
import { PortalService } from '../portal/portal.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  attemptedRoute: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null); // This route holds the route a user tried to reach when not logged in. After it has been used, it is nulled out

  private auth_portal: HttpClient;

  sessionToken: string;

  constructor(
    private http: HttpClient,
    public platform: Platform,
    private handler: HttpBackend,
    private storage: StorageService,
    private utilService: UtilService,
    private appUserService: AppuserService,
    private settings: AppSettingsService,
    private portalService: PortalService,
  ) {
    this.auth_portal = new HttpClient(this.handler);

    this.storage.get("Auth_Token").then(token => {
      if(token) {
        this.isAuthenticated.next(true);
      }
    });
  }

  login(email: string, password: string, deviceID?: string) : Observable<any> {
    const params = {
      email: email,
      password: this.utilService.jsonFriendlyUrlString(password),
      device_id: deviceID
    }

    const url = this.settings.apiUrl + `appUser/login?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: any) => {
        if (data?.status && data.token) {
          this.storage.set("Session_Token", data.token);
          this.sessionToken = data.token;
          this.isAuthenticated.next(true);

          Preferences.set({
            key: 'Session_Token',
            value: data.token,
          });

          return data;
        } else {
          return data;
        }
      }),
      catchError(err => {
        return of(err.error);
      })
    );
  }

  loginWithRinkNet(database: string, username: string, token: string) : Observable<any> {
    const params = {
      database: database,
      username: username,
      token: token
    }

    const url = this.settings.apiUrl + `appUser/loginWithRinknet`

    return this.http.post(url, params).pipe(
      map((data: any) => {
        if (data?.status && data.token) {
          this.storage.set("Session_Token", data.token);
          this.sessionToken = data.token;
          this.isAuthenticated.next(true);

          Preferences.set({
            key: 'Session_Token',
            value: data.token,
          });

          return data;
        } else {
          return data;
        }
      }),
      catchError(err => {
        return of(err.error);
      })
    );
  }

  getToken(user_service_id: number, auth_token: string) {
    const headers = { 'Authorization': auth_token };

    return this.auth_portal.get(`${environment.AUTHPORTAL_API_URL}services/token?userServiceId=${user_service_id.toString()}`, { headers });
  }

  linkAccounts(serviceUserID: number, authToken: string) : Observable<boolean> {
    const body = {
      service_user_id: serviceUserID,
      auth_token: authToken
    }

    const url = this.settings.apiUrl + `appUser/linkAccount`;

    return this.http.put(url, body).pipe(
        map((data: any) => {
          return true;
        }),
        catchError(err => {
          return of(err.error);
        })
      );
  }

  unlinkAccounts() : Observable<boolean> {
    const url = this.settings.apiUrl + `appUser/unlinkAccount`;

    return this.http.put(url, {}).pipe(
        map((data: any) => {
          return true;
        }),
        catchError(err => {
          return of(err.error);
        })
      );
  }

  getUserServices(authToken: string) : Observable<RinknetAttachment[]> {
    const params = {
        auth_token: authToken
    };

    const url = this.settings.apiUrl + `appUser/rinknetServices?parameters=${JSON.stringify(params)}`;

    return this.auth_portal.get(url).pipe(
      map((data: any) => {
        let rinknetTiles: RinknetAttachment[] = [];
        data.forEach((tile: any) => {
          let newTile = new RinknetAttachment(tile);
          if (newTile.serviceName == 'rinknet') {
            rinknetTiles.push(newTile);
          }
        });

        return rinknetTiles;
      })
    );
  }

  showUser() {
    const url = this.settings.apiUrl + `user`;

    return this.http.get(url).pipe(
      map((data: any) => {
        return data;
      })
    )
  }

  register(email: string, password: string, firstName: string, lastName: string) : Observable<any> {
    const body = {
      email: email,
      password: password,
      first_name: firstName,
      last_name: lastName
    }

    const url = this.settings.apiUrl + 'appUsers';

    return this.http.post(url, body).pipe(
      map((data: any) => {
        return data;
      }),
      catchError(err => {
        return of(err.error);
      })
    )
  }

  requestPasswordReset(email: string) : Observable<string> {
    const body = {
      email: email
    }

    const url = this.settings.apiUrl + 'requestPasswordReset';

    return this.http.post(url, body).pipe(
      map((data: any) => {
        if (data?.status) {
          return data.status;
        } else {
          return null;
        }
      }),
      catchError(err => {
        return of(err.error);
      })
    );
  }

  resetPassword(email: string, token: string, password: string) : Observable<string> {
    const body = {
      email: email,
      token: token,
      password: password
    }

    const url = this.settings.apiUrl + 'resetPassword';

    return this.http.post(url, body).pipe(
      map((data: any) => {
        if (data?.status) {
          return data.status;
        } else {
          return null;
        }
      }),
      catchError(err => {
        return of(err.error);
      })
    );
  }

  requestVerifyUser(email: string) : Observable<string> {
    const body = {
      email: email
    }

    const url = this.settings.apiUrl + 'requestVerifyUser';

    return this.http.post(url, body).pipe(
      map((data: any) => {
        if (data?.status) {
          return data.status;
        } else {
          return null;
        }
      }),
      catchError(err => {
        return of(err.error);
      })
    );
  }

  verifyUser(email: string, token: string) : Observable<boolean> {
    const body = {
      email: email,
      token: token
    }

    const url = this.settings.apiUrl + 'appUser/verifyUser';

    return this.http.post(url, body).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  verifyRinknetLogin(email: string, password: string) : Observable<[string, RinknetAttachment[]]> {
    const body = {
        email: email,
        password: password
      }
  
      const url = this.settings.apiUrl + 'appUser/verifyRinknetLogin';
  
      return this.http.post(url, body).pipe(
        map((data: any) => {
          if (data.http_status_code == 401) {
            return [null, null]
          } else {
            let authToken = data.token;
            let dataServices = data.services;
            let services: RinknetAttachment[] = [];
            dataServices.forEach((service: any) => {
              services.push(new RinknetAttachment(service));
            });
  
            return [authToken, services];
          }
        })
      );
  }

  async logout() {
    this.storage.clear().then(async () => { // Clear all stored data
      this.appUserService.appUser = null;
      this.portalService.portalRules.next({});
      this.portalService.portals = [];
      this.isAuthenticated.next(false); // Update ht auth guard
      await Preferences.remove({ key: 'Session_Token' });
    });
  }

  requestUpdateEmail(email: string) : Observable<string> {
    const body = {
      email: email
    }

    const url = this.settings.apiUrl + 'requestUpdateEmail';

    return this.http.post(url, body).pipe(
      map((data: any) => {
        if (data?.status) {
          return data.status;
        } else {
          return null;
        }
      }),
      catchError(err => {
        return of(err.error);
      })
    );
  }

  updateEmail(email: string, newEmail: string, token: string) : Observable<string> {
    const body = {
      email: email,
      newEmail: newEmail,
      token: token,
    }

    const url = this.settings.apiUrl + 'appuser/email';

    return this.http.put(url, body).pipe(
      map((data: any) => {
        if (data?.status) {
          return data.status;
        } else {
          return null;
        }
      }),
      catchError(err => {
        return of(err.error);
      })
    );
  }
}
